import React, { useState, useRef } from "react";
import { Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle2, X, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { API } from "../lib/api";
import { eur, formatDateIt, MONTHS_IT } from "../lib/format";
import PeriodFilter from "../components/PeriodFilter";

const now = new Date();

export default function DataManagement() {
  // --- Import state ---
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef(null);

  // --- Export state ---
  const [period, setPeriod] = useState({ year: undefined, month: undefined });
  const [exporting, setExporting] = useState(false);

  const resetImport = () => {
    setFile(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onFileSelected = (f) => {
    if (!f) return;
    if (!/\.(xlsx|xlsm)$/i.test(f.name)) {
      toast.error("Seleziona un file Excel (.xlsx)");
      return;
    }
    setFile(f);
    setPreview(null);
  };

  const runPreview = async () => {
    if (!file) return;
    setLoadingPreview(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("dry_run", "true");
      const res = await fetch(`${API}/data/import`, { method: "POST", body: form });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.detail || "Errore durante l'analisi");
        return;
      }
      setPreview(body);
      toast.success(`Analisi completata: ${body.new_count} nuove, ${body.duplicates_count} duplicate`);
    } catch (e) {
      toast.error("Errore di rete durante l'analisi");
    } finally {
      setLoadingPreview(false);
    }
  };

  const confirmImport = async () => {
    if (!file || !preview || preview.new_count === 0) return;
    setConfirming(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("dry_run", "false");
      const res = await fetch(`${API}/data/import`, { method: "POST", body: form });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.detail || "Errore durante l'import");
        return;
      }
      toast.success(`Importate ${body.imported} nuove spese!`);
      resetImport();
    } catch (e) {
      toast.error("Errore di rete durante l'import");
    } finally {
      setConfirming(false);
    }
  };

  const runExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (period.year) params.set("year", String(period.year));
      if (period.month) params.set("month", String(period.month));
      const url = `${API}/data/export${params.toString() ? `?${params}` : ""}`;
      const res = await fetch(url);
      if (!res.ok) {
        toast.error("Errore durante l'export");
        return;
      }
      const blob = await res.blob();
      const dispo = res.headers.get("Content-Disposition") || "";
      const m = dispo.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : "spese.xlsx";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("File scaricato");
    } catch (e) {
      toast.error("Errore di rete durante l'export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="data-management-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Gestione</div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold leading-tight">Gestione Dati</h1>
        <p className="text-stone-600 mt-2 max-w-2xl">Importa nuove spese da un file Excel oppure esporta tutte le spese (opzionalmente filtrate per periodo).</p>
      </div>

      {/* ---------------- IMPORT ---------------- */}
      <section className="card-soft p-8" data-testid="import-section">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-[#377D22] text-white grid place-items-center">
            <Upload size={22} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-2xl font-semibold leading-tight">Importa Excel</h2>
            <p className="text-stone-600 text-sm mt-1">
              Il file deve avere le colonne: <span className="font-mono text-xs bg-stone-100 px-1.5 py-0.5 rounded">DATA · PERSONA · CATEGORIA · SOTTO-CATEGORIA · DESCRIZIONE · IMPORTO</span>.
              I duplicati vengono saltati automaticamente. Persona accetta <em>Madda</em>, <em>Doio</em> o <em>Famiglia</em> (split 50/50).
            </p>
          </div>
        </div>

        {/* Dropzone */}
        <div
          className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${file ? "border-[#2E5F1B] bg-stone-50" : "border-stone-300 hover:border-stone-400 bg-white"}`}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); onFileSelected(e.dataTransfer.files?.[0]); }}
          data-testid="import-dropzone"
        >
          <FileSpreadsheet size={40} className="mx-auto text-stone-400 mb-3" strokeWidth={1.2} />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <span className="font-medium" data-testid="import-filename">{file.name}</span>
              <button onClick={resetImport} className="pill-btn p-1 text-stone-500 hover:bg-stone-200" data-testid="import-clear" aria-label="Rimuovi file">
                <X size={16} />
              </button>
            </div>
          ) : (
            <>
              <p className="text-stone-600 mb-2">Trascina qui il file Excel oppure</p>
              <Button onClick={() => inputRef.current?.click()} className="pill-btn bg-[#377D22] hover:bg-[#2E5F1B]" data-testid="import-browse">
                Scegli file
              </Button>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => onFileSelected(e.target.files?.[0])}
            data-testid="import-file-input"
          />
        </div>

        {file && !preview && (
          <div className="mt-4 flex justify-end">
            <Button onClick={runPreview} disabled={loadingPreview} className="pill-btn bg-[#377D22] hover:bg-[#2E5F1B] h-11 px-6" data-testid="import-preview-btn">
              {loadingPreview ? (<><Loader2 size={16} className="animate-spin mr-2" /> Analisi…</>) : "Analizza file"}
            </Button>
          </div>
        )}

        {preview && (
          <div className="mt-6 space-y-4" data-testid="import-preview">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Nuove" value={preview.new_count} accent="#4A6D5E" testid="stat-new" />
              <StatCard label="Duplicate" value={preview.duplicates_count} accent="#A8A29E" testid="stat-dup" />
              <StatCard label="Categorie sconosciute" value={preview.invalid_category_count} accent="#C0392B" testid="stat-invalid" />
              <StatCard label="Errori riga" value={preview.parse_errors_count} accent="#C0392B" testid="stat-errors" />
            </div>

            {Object.keys(preview.new_subcategories || {}).length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="new-subs-banner">
                Saranno aggiunte queste nuove sotto-categorie:
                <ul className="mt-1 list-disc list-inside">
                  {Object.entries(preview.new_subcategories).map(([cat, subs]) => (
                    <li key={cat}><span className="font-semibold">{cat}</span>: {subs.join(", ")}</li>
                  ))}
                </ul>
              </div>
            )}

            {preview.invalid_category.length > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm" data-testid="invalid-cat-banner">
                <div className="flex items-center gap-2 text-red-800 font-semibold mb-1">
                  <AlertTriangle size={16} /> Righe con categoria inesistente (saltate):
                </div>
                <ul className="text-red-700 max-h-32 overflow-auto">
                  {preview.invalid_category.map((r) => (
                    <li key={`inv-${r.row}`} className="font-mono text-xs">riga {r.row}: «{r.category}» ({r.person} · {r.description || "—"})</li>
                  ))}
                </ul>
                <p className="text-xs text-red-700 mt-2">Crea prima la categoria in <strong>Impostazioni</strong> o correggi il nome nel file.</p>
              </div>
            )}

            {preview.parse_errors.length > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm" data-testid="parse-errors-banner">
                <div className="flex items-center gap-2 text-red-800 font-semibold mb-1">
                  <AlertTriangle size={16} /> Errori di parsing:
                </div>
                <ul className="text-red-700 max-h-32 overflow-auto">
                  {preview.parse_errors.map((r) => (
                    <li key={`err-${r.row}`} className="font-mono text-xs">riga {r.row}: {r.error}</li>
                  ))}
                </ul>
              </div>
            )}

            {preview.new_count > 0 && (
              <div className="rounded-xl border border-stone-200 overflow-hidden">
                <div className="bg-stone-50 px-4 py-2 text-xs uppercase tracking-widest text-stone-500 flex items-center gap-2">
                  <CheckCircle2 size={14} /> Anteprima nuove spese ({preview.new_count})
                </div>
                <div className="max-h-72 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white sticky top-0">
                      <tr className="text-xs uppercase tracking-widest text-stone-500 text-left">
                        <th className="px-4 py-2">Data</th>
                        <th className="px-4 py-2">Persona</th>
                        <th className="px-4 py-2">Categoria</th>
                        <th className="px-4 py-2">Sotto-cat.</th>
                        <th className="px-4 py-2">Descrizione</th>
                        <th className="px-4 py-2 text-right">Importo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview_new.map((r) => (
                        <tr key={`new-${r.row}`} className="border-t border-stone-100">
                          <td className="px-4 py-2 tabular text-stone-600 whitespace-nowrap">{formatDateIt(r.date)}</td>
                          <td className="px-4 py-2">{r.person}</td>
                          <td className="px-4 py-2">{r.category}</td>
                          <td className="px-4 py-2 text-stone-500">{r.subcategory || "—"}</td>
                          <td className="px-4 py-2 max-w-[280px] truncate">{r.description || "—"}</td>
                          <td className="px-4 py-2 text-right tabular font-semibold">{eur(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
              <Button onClick={resetImport} variant="ghost" className="rounded-full" data-testid="import-cancel">Annulla</Button>
              <Button
                onClick={confirmImport}
                disabled={preview.new_count === 0 || confirming}
                className="pill-btn bg-[#377D22] hover:bg-[#2E5F1B] h-11 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="import-confirm-btn"
              >
                {confirming ? (<><Loader2 size={16} className="animate-spin mr-2" /> Importazione…</>) : `Importa ${preview.new_count} spese`}
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* ---------------- EXPORT ---------------- */}
      <section className="card-soft p-8" data-testid="export-section">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-[#377D22] text-white grid place-items-center">
            <Download size={22} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-2xl font-semibold leading-tight">Esporta Excel</h2>
            <p className="text-stone-600 text-sm mt-1">
              Scarica un file <span className="font-mono text-xs bg-stone-100 px-1.5 py-0.5 rounded">.xlsx</span> con tutte le spese. Filtra per anno/mese se vuoi un sottoinsieme.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <PeriodFilter idPrefix="exp-data" year={period.year} month={period.month} onChange={setPeriod} />
          <div className="ml-auto">
            <Button onClick={runExport} disabled={exporting} className="pill-btn bg-[#377D22] hover:bg-[#2E5F1B] h-11 px-6" data-testid="export-btn">
              {exporting ? (<><Loader2 size={16} className="animate-spin mr-2" /> Generazione…</>) : (<><Download size={16} className="mr-2" /> Scarica Excel</>)}
            </Button>
          </div>
        </div>
        <p className="text-xs text-stone-500 mt-3" data-testid="export-hint">
          {period.year || period.month
            ? `Esporto: ${period.month ? MONTHS_IT[period.month - 1] + " " : ""}${period.year || "tutti gli anni"}`
            : "Esporto tutte le spese di tutti i periodi"}
        </p>
      </section>
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className="card-soft p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">{label}</div>
      <div className="font-display text-3xl font-semibold tabular" style={{ color: accent }}>{value}</div>
    </div>
  );
}
